"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ApplicationStateProvider = exports.useApplicationStateValueStructure = exports.useApplicationStateValue = exports.ApplicationStateContext = exports.getApplicationStateValueStructure = exports.setApplicationStateValue = exports.setApplicationStateModified = exports.getApplicationStateValue = exports.getApplicationStateModified = exports.getApplicationStateIdentifier = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const getApplicationStateIdentifier = (subStateIdMap) => (subStateIdMap ? subStateIdMap : {});
exports.getApplicationStateIdentifier = getApplicationStateIdentifier;
const getApplicationStateModified = (identifier, modificationState) => !!modificationState.get(identifier);
exports.getApplicationStateModified = getApplicationStateModified;
const getApplicationStateValue = (identifier, applicationState) => applicationState.get(identifier);
exports.getApplicationStateValue = getApplicationStateValue;
const setApplicationStateModified = (identifier, value, modificationState) => new Map(modificationState).set(identifier, value);
exports.setApplicationStateModified = setApplicationStateModified;
const setApplicationStateValue = (identifier, value, applicationState) => new Map(applicationState).set(identifier, value);
exports.setApplicationStateValue = setApplicationStateValue;
const getApplicationStateValueStructure = (idStructure, applicationState) => Object.keys(idStructure).reduce((acc, k) => (Object.assign(Object.assign({}, acc), { [k]: (0, exports.getApplicationStateValue)(idStructure[k], applicationState) })), {});
exports.getApplicationStateValueStructure = getApplicationStateValueStructure;
exports.ApplicationStateContext = (0, react_1.createContext)({
    modified: new Map(),
    value: new Map(),
    onChange: () => { },
    setModified: () => { },
});
const { Provider } = exports.ApplicationStateContext;
/**
 * Get and set an application state value by identifier.
 * */
const useApplicationStateValue = (identifier) => {
    const { modified: modificationState, value: applicationState, onChange: setApplicationState, setModified: setModificationState, } = (0, react_1.useContext)(exports.ApplicationStateContext);
    const appStateRef = (0, react_1.useRef)(applicationState);
    appStateRef.current = applicationState;
    const modified = (0, react_1.useMemo)(() => (0, exports.getApplicationStateModified)(identifier, modificationState), [identifier, modificationState]);
    const value = (0, react_1.useMemo)(() => (0, exports.getApplicationStateValue)(identifier, applicationState), [identifier, applicationState]);
    const setModified = (0, react_1.useCallback)((isModified) => {
        setModificationState((0, exports.setApplicationStateModified)(identifier, isModified, modificationState));
    }, [identifier, setModificationState]);
    const onChange = (0, react_1.useCallback)((newValue) => {
        setApplicationState((0, exports.setApplicationStateValue)(identifier, newValue, appStateRef.current));
        setModified(true);
    }, [identifier, setApplicationState]);
    const controller = (0, react_1.useMemo)(() => ({
        modified,
        value,
        onChange,
        setModified,
    }), [modified, onChange, setModified, value]);
    return controller;
};
exports.useApplicationStateValue = useApplicationStateValue;
/**
 * Use an object that is a collection of application state value controllers.
 * */
const useApplicationStateValueStructure = (idStructure) => {
    const { value: applicationState, onChange: setApplicationState } = (0, react_1.useContext)(exports.ApplicationStateContext);
    const valueStructure = (0, react_1.useMemo)(() => (0, exports.getApplicationStateValueStructure)(idStructure, applicationState), [applicationState, idStructure]);
    const onChangeStructure = (0, react_1.useMemo)(() => Object.keys(idStructure).reduce((acc, k) => (Object.assign(Object.assign({}, acc), { [k]: (newValue) => {
            setApplicationState((0, exports.setApplicationStateValue)(idStructure[k], newValue, applicationState));
        } })), {}), [applicationState, idStructure, setApplicationState]);
    const controller = (0, react_1.useMemo)(() => ({
        valueStructure,
        onChangeStructure,
    }), [onChangeStructure, valueStructure]);
    return controller;
};
exports.useApplicationStateValueStructure = useApplicationStateValueStructure;
/**
 * Provide a context container for application state.
 * */
const ApplicationStateProvider = ({ children, }) => {
    const [modified, setModified] = (0, react_1.useState)(new Map());
    const [value, setValue] = (0, react_1.useState)(new Map());
    const controller = (0, react_1.useMemo)(() => ({
        modified,
        value,
        onChange: setValue,
        setModified,
    }), [modified, value]);
    return (0, jsx_runtime_1.jsx)(Provider, { value: controller, children: children });
};
exports.ApplicationStateProvider = ApplicationStateProvider;
